import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { MeResponseSchema, type MeResponse } from '@qs/shared';
import { UNAUTHORIZED_EVENT, type UnauthorizedDetail } from '@qs/ui/lib/api';
import { ApiError, api } from './api';

type AuthState = {
  me: MeResponse | null;
  loading: boolean;
  refresh: () => Promise<MeResponse | null>;
  logout: () => Promise<void>;
};

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get('/me', MeResponseSchema);
      setMe(r);
      return r;
    } catch (e) {
      if (!(e instanceof ApiError) || e.status === 401) setMe(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await api.post('/auth/logout').catch(() => {});
    setMe(null);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);
  // ログインが切れたら（どの API でも 401）、ログインが要る画面はログイン画面へ移す。タブに戻ったときも確かめる（2026-10-12）
  useEffect(() => {
    const onUnauthorized = (e: Event) => {
      if ((e as CustomEvent<UnauthorizedDetail>).detail.base === '/api') setMe(null);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  return <Ctx.Provider value={{ me, loading, refresh, logout }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error('AuthProvider がありません');
  return c;
}

/** ログインが必要な画面 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div role="status" style={{ padding: 32 }}>読み込み中…</div>;
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <>{children}</>;
}
